'use client';
import { sendOnbEvent } from '@/components/newRelic';
import { useStepData } from '@/components/stepData';
import { RootState } from '@/redux/store';
import { Button } from '@mui/material';
import { rejection } from '@repo/onb-api';
import { ResultScreen } from '@repo/ui';
import { formatter, getClientCookie } from '@repo/utils';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

const isOnbCommerce = process.env.NEXT_PUBLIC_ONB_INSTANCE_TYPE === 'onb-comercios';
const isOnbCuotas = process.env.NEXT_PUBLIC_ONB_INSTANCE_TYPE === 'onb-cuotas';

function renderOnbCommerceMessage(rejectionReason?: string) {
  switch (rejectionReason) {
    case 'IDENTITY_VERIFICATION_ATTEMPTS_LIMIT':
    case 'IDENTITY_VERIFICATION_PROCESS_FAILED':
      return <>No pudimos validar tu identidad. Podés intentarlo en otro momento y seguro te vamos a ayudar.</>;
    case 'CUSTOMER_RISK_EVALUATION_REJECTED':
    case 'ORDER_EXCEEDS_APPROVED_AMOUNT':
      return <>En este momento no podemos continuar con tu solicitud.</>;
    case 'TIME_OUT':
      return <>Tu tiempo de sesión se agotó, volvé a intentarlo.</>;
    case 'INSUFFICIENT_FUNDS':
      return (
        <>
          Revisá con el emisor de tu tarjeta si los límites de operación diarios son correctos y si tu tarjeta tiene los
          fondos necesarios para completar la transacción.
        </>
      );
    case 'PAYMENT_GATEWAY_ERROR':
      return <>El pago no ha sido autorizado. Contactá al emisor de tu tarjeta para solicitar autorización.</>;
    case 'INVALID_MERCHANT_IDENTIFICATION':
      return <>Tu CUIT ya ha sido utilizado. Comunicate con nuestro equipo de soporte para más información.</>;
    default:
      return <>En este momento no podemos continuar con tu solicitud.</>;
  }
}

function renderDefaultMessage(rejectionReason?: string, policyMaximumAmount?: number) {
  switch (rejectionReason) {
    case 'IDENTITY_VERIFICATION_ATTEMPTS_LIMIT':
    case 'IDENTITY_VERIFICATION_PROCESS_FAILED':
      return <>No pudimos validar tu identidad. Podés intentarlo en otro momento y seguro te vamos a ayudar.</>;
    case 'CUSTOMER_RISK_EVALUATION_REJECTED':
    case 'ORDER_EXCEEDS_APPROVED_AMOUNT':
      if (isOnbCuotas && policyMaximumAmount) {
        return (
          <>
            <strong>Podés comprar hasta {formatter(policyMaximumAmount, 'currency')}</strong> y tu carrito excede ese
            importe.
          </>
        );
      }
      return <>No tenemos una oferta disponible.</>;
    case 'TIME_OUT':
      return <>Tu tiempo de sesión se agotó, volvé a intentarlo.</>;
    case 'INSUFFICIENT_FUNDS':
      return (
        <>
          Revisá con el emisor de tu tarjeta si los límites de operación diarios son correctos y si tu tarjeta tiene los
          fondos necesarios para completar la transacción.
        </>
      );
    case 'PAYMENT_GATEWAY_ERROR':
      return <>El pago no ha sido autorizado. Contactá al emisor de tu tarjeta para solicitar autorización.</>;
    case 'INVALID_MERCHANT_IDENTIFICATION':
      return <>Tu CUIT ya ha sido utilizado. Comunicate con nuestro equipo de soporte para más información.</>;
    default:
      return <>No tenemos una oferta disponible.</>;
  }
}

export default function Rejection({ stepdata: stepDataProp }: { stepdata?: rejection['variables'] }) {
  const stepData = useStepData(stepDataProp);
  const { firstName } = useSelector((state: RootState) => state.flowdata.variables) || {};
  const policyMaximumAmount = stepData?.policyMaximumAmount;
  const rejectionReason = stepData?.rejectionReason;
  const router = useRouter();
  const internal = useSelector((state: RootState) => state.additionalData.internal);
  const whatsappNumber = isOnbCommerce ? '5491157494024' : '5491131878888';
  useEffect(() => {
    sendOnbEvent({
      type: 'interaction',
      subtype: 'rejected',
      data: { rejectionReason, instanceId: getClientCookie('instanceId') },
    });
  }, []);

  const rejectionMessage = isOnbCommerce
    ? renderOnbCommerceMessage(rejectionReason)
    : renderDefaultMessage(rejectionReason, policyMaximumAmount);

  const onbCommerceRejectionCodes: Record<string, string> = {
    INVALID_MERCHANT_IDENTIFICATION: 'Código 1',
    WATCHLIST_REPET_MATCH: 'Código 2',
    SMS_OTP_VALIDATION_ATTEMPTS_LIMIT: 'Código 3',
    MERCHANT_RISK_EVALUATION_REJECTED: 'Código 4',
    MERCHANT_RISK_EVALUATION_INVALID_LEGAL_FORM: 'Código 5',
    IDENTITY_VERIFICATION_ATTEMPTS_LIMIT: 'Código 6',
    TIME_OUT: 'Código 7',
  };

  const codeExists = rejectionReason && Object.keys(onbCommerceRejectionCodes).includes(rejectionReason);

  return (
    <ResultScreen
      status="error"
      code={isOnbCommerce && rejectionReason && codeExists ? onbCommerceRejectionCodes[rejectionReason] : undefined}
      title={firstName ? `Lo sentimos, ${formatter(firstName, 'capitalize')}` : 'Lo sentimos'}
      description={rejectionMessage}
      supportHref={rejectionReason !== 'PAYMENT_GATEWAY_ERROR' ? `https://wa.me/${whatsappNumber}` : undefined}
      actions={
        !internal &&
        (isOnbCuotas ? (
          <Button fullWidth variant="contained" id="button-cancel" onClick={() => router.push('/back_to_store')}>
            Volver a la tienda
          </Button>
        ) : (
          <Button fullWidth variant="contained" id="button-cancel" onClick={() => router.push('/')}>
            Volver al inicio
          </Button>
        ))
      }
    />
  );
}
